
import { Component } from "@/utils/types/componentTypes";
import { ComponentTypeDialog } from "@/components/component-types/ComponentTypeDialog";
import { CreateComponentDialog } from "@/components/components/CreateComponentDialog";
import { useAuth } from "@/context/AuthContext";
import { useOrganization } from "@/context/OrganizationContext";
import { SidebarHeader } from "./sidebar/SidebarHeader";
import { ComponentFilters } from "./sidebar/ComponentFilters";
import { ComponentsList } from "./sidebar/ComponentsList";
import { useSidebarState } from "./sidebar/useSidebarState";
import { useSidebarActions } from "./sidebar/useSidebarActions";

interface ComponentSidebarProps {
  components: Component[];
  loading: boolean;
  diagramName?: string;
  refreshComponents?: () => Promise<void>;
}

export function ComponentSidebar({ 
  components, 
  loading, 
  diagramName,
  refreshComponents 
}: ComponentSidebarProps) {
  const { subscriptionData } = useAuth();
  const { organizations, currentOrganization } = useOrganization();
  
  const {
    searchTerm,
    setSearchTerm,
    organizationFilter,
    setOrganizationFilter,
    createTypeDialogOpen,
    setCreateTypeDialogOpen,
    createComponentDialogOpen,
    setCreateComponentDialogOpen,
    isSaving,
    setIsSaving,
    expandedGroups,
    toggleGroup,
    componentTypes,
    setComponentTypes,
    loadingComponentTypes,
    setLoadingComponentTypes,
  } = useSidebarState();

  const {
    handleCreateComponentType,
    handleCreateComponent,
    handleAddNew,
    handleBack,
  } = useSidebarActions(
    subscriptionData,
    currentOrganization,
    refreshComponents,
    setIsSaving,
    setCreateTypeDialogOpen,
    setCreateComponentDialogOpen,
    setComponentTypes,
    setLoadingComponentTypes
  );

  const onDragStart = (event: React.DragEvent, component: Component) => {
    // Detectar elementos especiais e definir o tipo correto de nó
    let nodeType = 'componentNode';
    if (component.id === 'moldura-element') {
      nodeType = 'labeledGroup';
    } else if (component.id === 'texto-element') {
      nodeType = 'textNode';
    } else if (component.id === 'ator-element') {
      nodeType = 'actorNode';
    }
    
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('application/componentId', component.id);
    event.dataTransfer.setData('application/componentName', component.name);
    event.dataTransfer.setData('application/componentTypeId', component.id_component_type);
    event.dataTransfer.setData('application/componentTypeName', component.component_types?.name || '');
    event.dataTransfer.setData('application/componentIcon', component.component_types?.icon || '');
    event.dataTransfer.setData('application/organizationId', component.id_organization);
    event.dataTransfer.setData('application/organizationName', (component as any).organizations?.name || '');
    event.dataTransfer.setData('application/codeChanges7Days', String(component.code_changes_7days || 0));
    event.dataTransfer.setData('application/codeChanges30Days', String(component.code_changes_7days || 0));
    event.dataTransfer.setData('application/deploys7Days', String(component.deploys_7days || 0));
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="w-64 bg-sidebar border-r border-sidebar-border h-full flex flex-col">
      {/* Header */}
      <SidebarHeader
        diagramName={diagramName}
        onBack={handleBack}
      />

      <div className="px-4 mb-4">
        <ComponentFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          organizationFilter={organizationFilter}
          onOrganizationFilterChange={setOrganizationFilter}
          organizations={organizations}
        />
      </div>

      {/* Components List */}
      <div className="flex-1 overflow-y-auto">
        <ComponentsList
          components={components}
          loading={loading}
          searchTerm={searchTerm}
          organizationFilter={organizationFilter}
          expandedGroups={expandedGroups}
          onToggleGroup={toggleGroup}
          onDragStart={onDragStart}
        />
      </div>

      <ComponentTypeDialog
        open={createTypeDialogOpen}
        onOpenChange={setCreateTypeDialogOpen}
        onSave={handleCreateComponentType}
        componentType={null}
        isLoading={isSaving}
        mode="create"
      />

      <CreateComponentDialog
        open={createComponentDialogOpen}
        onOpenChange={setCreateComponentDialogOpen}
        onSave={handleCreateComponent}
        componentTypes={componentTypes}
        isLoading={isSaving}
      />
    </div>
  );
}
